import React, { useEffect, useRef } from 'react';
import { useDisclosure } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { atsJobsApiActions, atsJobsActions } from '@/store/atsJobs';
import { getAtsJobs } from '@/store/atsJobs';
import { useShowToast } from '@/hooks';
import { JobCreateEditModal } from '@/pages/ats-jobs/components/JobCreateEditModal';
import { API_SERVICE } from '../api';

interface UseCreateJobForRequisitionProps {
  requisitionId: string;
  onSuccess?: () => void;
  requisition?: any;
}

interface UseCreateJobForRequisitionReturn {
  modalActions: {
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
    onToggle: () => void;
    isControlled: boolean;
    getButtonProps: (props?: any) => any;
    getDisclosureProps: (props?: any) => any;
  };
  config: any;
  modal: React.ReactNode;
}

export const useCreateJobForRequisition = ({
  requisitionId,
  onSuccess,
  requisition,
}: UseCreateJobForRequisitionProps): UseCreateJobForRequisitionReturn => {
  const modalActions = useDisclosure();
  const dispatch = useDispatch();
  const history = useHistory();
  const toast = useShowToast();
  const { resetJobState } = atsJobsActions;
  const { createJob = {} } = useSelector(getAtsJobs);
  const createdJobId = createJob?.data?.id;
  const isAssociatingRef = useRef(false);
  const isPartOfGroup = !!requisition?.requisition_group_id;

  // Watch for job creation and then associate it
  useEffect(() => {
    const associateJobWithRequisition = async () => {
      if (createdJobId && !isAssociatingRef.current) {
        isAssociatingRef.current = true;
        try {
          const payload: any = {
            ats_job_id: createdJobId,
          };

          if (isPartOfGroup) {
            payload.apply_to_group = true;
          }

          await API_SERVICE.associateJobToRequisition(requisitionId, payload);

          toast({
            title: 'Job created and associated successfully',
            status: 'success',
          });

          if (onSuccess) {
            onSuccess();
          }

          // Redirect to the job page
          history.push(`/jobs/${createdJobId}`);
        } catch (error: any) {
          toast({
            title: 'Failed to associate job',
            description: error?.message || 'Please try again',
            status: 'error',
          });
        } finally {
          isAssociatingRef.current = false;
        }
      }
    };

    associateJobWithRequisition();
  }, [
    createdJobId,
    requisitionId,
    toast,
    onSuccess,
    history,
    requisition,
    isPartOfGroup,
  ]);

  const config = {
    id: 'createJob',
    label: 'Create new job',
    description: isPartOfGroup
      ? 'Create a job and associate it with this requisition and all related requisitions'
      : 'Create a job and associate it with this requisition',
    modalActionText: 'Create and associate',
    loadingText: 'Creating job...',
    skipRedirect: true,
    reFetch: () => {
      dispatch(resetJobState({ key: 'createJob' }));
    },
    apiCall: (payload: any) =>
      dispatch(
        atsJobsApiActions('createJob', {
          requestConfig: payload,
        })
      ),
    initialValues: {
      name: requisition?.role_name || '',
    },
  };

  const modal = modalActions.isOpen && (
    <JobCreateEditModal modalActions={modalActions} config={config} />
  );

  return {
    modalActions,
    config,
    modal,
  };
};
